import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, XCircle, Clock, AlertCircle, Download, FileText, Image as ImageIcon, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const ApproveHours = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axiosInstance.get('/time-entries?limit=200');
      const data = response.data.entries || response.data || [];
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (entry) => {
    setEditingId(entry.id);
    setEditData({
      date: entry.date,
      start_time: entry.start_time,
      end_time: entry.end_time,
      hours_worked: entry.hours_worked,
      travel_hours: entry.travel_hours || 0,
      lunch_hours: entry.lunch_hours || 0,
      project: entry.project || '',
      description: entry.description || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const calculateHours = (start, end, lunch) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const totalMinutes = (eh * 60 + em) - (sh * 60 + sm);
    const lunchMinutes = parseFloat(lunch) || 0;
    return Math.max(0, (totalMinutes - lunchMinutes) / 60);
  };

  const saveEdit = async (entryId) => {
    try {
      const hours = calculateHours(editData.start_time, editData.end_time, editData.lunch_hours);
      const formData = new FormData();
      formData.append('date', editData.date);
      formData.append('start_time', editData.start_time);
      formData.append('end_time', editData.end_time);
      formData.append('hours_worked', hours.toString());
      formData.append('travel_hours', editData.travel_hours.toString());
      formData.append('lunch_hours', editData.lunch_hours.toString());
      formData.append('project', editData.project);
      formData.append('description', editData.description);

      // Find original entry to preserve client
      const original = entries.find(e => e.id === entryId);
      if (original) {
        formData.append('client', original.client);
      }

      await axiosInstance.put(`/time-entries/${entryId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Registro atualizado!');
      setEditingId(null);
      fetchEntries();
    } catch (error) {
      toast.error('Erro ao atualizar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(`/time-entries/${id}/status?status=approved`);
      toast.success('Horas aprovadas!');
      fetchEntries();
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Deseja rejeitar este registro?')) return;
    try {
      await axiosInstance.put(`/time-entries/${id}/status?status=rejected`);
      toast.success('Registro rejeitado');
      fetchEntries();
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const handleDownload = async (entryId, fileId, filename) => {
    try {
      const response = await axiosInstance.get(`/time-entries/${entryId}/file/${fileId}`);
      const fileData = response.data.file_data;
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <ImageIcon className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const filteredEntries = entries.filter((e) => {
    if (filter === 'all') return true;
    return e.status === filter;
  });

  const pendingCount = entries.filter((e) => e.status === 'pending').length;
  const approvedCount = entries.filter((e) => e.status === 'approved').length;
  const rejectedCount = entries.filter((e) => e.status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-800 text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approve-hours-container">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Aprovar Horas</h1>
        <p className="text-gray-500 text-lg">Gerencie e edite as horas antes de aprovar</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-gray-800 text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-2 text-yellow-500" />
              Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-800">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-gray-800 text-sm flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Aprovadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-800">{approvedCount}</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-gray-800 text-sm flex items-center">
              <XCircle className="w-4 h-4 mr-2 text-red-500" />
              Rejeitadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-800">{rejectedCount}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" onValueChange={setFilter}>
        <TabsList className="bg-gray-50 border border-gray-200">
          <TabsTrigger value="pending">Pendentes ({pendingCount})</TabsTrigger>
          <TabsTrigger value="approved">Aprovadas</TabsTrigger>
          <TabsTrigger value="rejected">Rejeitadas</TabsTrigger>
          <TabsTrigger value="all">Todas</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="space-y-4 mt-6">
          {filteredEntries.length === 0 ? (
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum registro encontrado</p>
              </CardContent>
            </Card>
          ) : (
            filteredEntries.map((entry) => {
              const isEditing = editingId === entry.id;
              return (
                <Card key={entry.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`entry-card-${entry.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <CardTitle className="text-gray-800 text-lg">
                            {isEditing ? (
                              <Input
                                value={editData.project}
                                onChange={(e) => setEditData({ ...editData, project: e.target.value })}
                                className="bg-gray-50 border-gray-300 text-gray-800 font-bold h-8 w-64"
                                data-testid={`edit-project-${entry.id}`}
                              />
                            ) : (
                              entry.project
                            )}
                          </CardTitle>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            entry.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                            : entry.status === 'approved' ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                          }`}>
                            {entry.status === 'pending' ? 'Pendente' : entry.status === 'approved' ? 'Aprovada' : 'Rejeitada'}
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm">{entry.client}</p>
                        <p className="text-[#3da5a5] text-sm font-medium">{entry.user_name}</p>
                      </div>
                      <div className="flex gap-2">
                        {entry.status === 'pending' && !isEditing && (
                          <>
                            <Button
                              onClick={() => startEditing(entry)}
                              variant="outline"
                              size="sm"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100"
                              data-testid={`edit-entry-${entry.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              onClick={() => handleApprove(entry.id)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                              size="sm"
                              data-testid={`approve-entry-${entry.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprovar
                            </Button>
                            <Button
                              onClick={() => handleReject(entry.id)}
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              size="sm"
                              data-testid={`reject-entry-${entry.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {isEditing && (
                          <>
                            <Button
                              onClick={() => saveEdit(entry.id)}
                              className="bg-[#3da5a5] hover:bg-[#2d8585] text-white"
                              size="sm"
                              data-testid={`save-edit-${entry.id}`}
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Salvar
                            </Button>
                            <Button
                              onClick={() => { saveEdit(entry.id).then(() => handleApprove(entry.id)); }}
                              className="bg-green-500 hover:bg-green-600 text-white"
                              size="sm"
                              data-testid={`save-approve-${entry.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Salvar e Aprovar
                            </Button>
                            <Button
                              onClick={cancelEditing}
                              variant="ghost"
                              className="text-gray-500 hover:bg-gray-100"
                              size="sm"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isEditing ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <Label className="text-gray-500 text-xs">Data</Label>
                          <Input
                            type="date"
                            value={editData.date}
                            onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                            data-testid={`edit-date-${entry.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Início</Label>
                          <Input
                            type="time"
                            value={editData.start_time}
                            onChange={(e) => setEditData({ ...editData, start_time: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                            data-testid={`edit-start-${entry.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Fim</Label>
                          <Input
                            type="time"
                            value={editData.end_time}
                            onChange={(e) => setEditData({ ...editData, end_time: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                            data-testid={`edit-end-${entry.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Almoço (min)</Label>
                          <Input
                            type="number"
                            value={editData.lunch_hours}
                            onChange={(e) => setEditData({ ...editData, lunch_hours: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                            data-testid={`edit-lunch-${entry.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Viagem (h)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={editData.travel_hours}
                            onChange={(e) => setEditData({ ...editData, travel_hours: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <Label className="text-gray-500 text-xs">Descrição</Label>
                          <Input
                            value={editData.description}
                            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                            className="bg-gray-50 border-gray-300 text-gray-800 h-9"
                            data-testid={`edit-desc-${entry.id}`}
                          />
                        </div>
                        <div className="md:col-span-4 text-sm text-gray-500">
                          Horas calculadas: <strong className="text-[#0d3d3d]">
                            {calculateHours(editData.start_time, editData.end_time, editData.lunch_hours).toFixed(1)}h
                          </strong>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                          <div>
                            <span className="text-gray-400">Data</span>
                            <p className="text-gray-800 font-medium">{entry.date}</p>
                          </div>
                          <div>
                            <span className="text-gray-400">Horário</span>
                            <p className="text-gray-800 font-medium">{entry.start_time} - {entry.end_time}</p>
                          </div>
                          <div>
                            <span className="text-gray-400">Horas Trabalhadas</span>
                            <p className="text-gray-800 font-medium">{entry.hours_worked}h</p>
                          </div>
                          {entry.travel_hours > 0 && (
                            <div>
                              <span className="text-gray-400">Viagem</span>
                              <p className="text-gray-800 font-medium">{entry.travel_hours}h</p>
                            </div>
                          )}
                        </div>
                        {entry.description && (
                          <div className="mb-4">
                            <span className="text-gray-400 text-sm">Descrição</span>
                            <p className="text-gray-800 text-sm">{entry.description}</p>
                          </div>
                        )}
                      </>
                    )}
                    {entry.files && entry.files.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <span className="text-gray-400 text-sm block mb-2">Arquivos</span>
                        <div className="flex flex-wrap gap-2">
                          {entry.files.map((file) => (
                            <Button
                              key={file.id}
                              onClick={() => handleDownload(entry.id, file.id, file.filename)}
                              variant="outline"
                              size="sm"
                              className="bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 text-xs"
                            >
                              {getFileIcon(file.file_type)}
                              <span className="ml-2 max-w-[150px] truncate">{file.filename}</span>
                              <Download className="w-3 h-3 ml-2" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ApproveHours;
